import { useState, ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { Plan, Discount, OrderContext } from '@localstack/types';
import { CheckoutComponent, CheckoutFormSkeleton } from '@localstack/ui';
import { VAT_COUNTRIES } from '@localstack/constants';

import {
  useRoutes,
  useApiGetter,
  useApiEffect,
  SubscriptionService,
  AdminService,
  OrganizationService,
} from '@localstack/services';

import { AdminLayout } from '~/layouts';
import { AppRoute } from '~/config';
import { StripeCardForm } from '~/components';

export const Subscribe = (): ReactElement => {
  const { orgId, subscriptionId } = useParams<'orgId' | 'subscriptionId'>() as {
    orgId: string;
    subscriptionId?: string;
  };

  const { goto } = useRoutes();

  const [promotion, setPromotion] = useState<Optional<Discount>>();
  const [seats, setSeats] = useState<number>(1);
  const [plan, setPlan] = useState<Optional<Plan>>(null);

  const [stripeLoading, setStripeLoading] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);

  const { data: organization, isLoading: isOrgLoading } = useApiGetter(OrganizationService, 'adminGetOrganization', [
    orgId,
  ]);
  const { data: resources, isLoading: isResourcesLoading } = useApiGetter(AdminService, 'listResources', []);
  const { getPromotion, isLoading: isPromotionLoading } = useApiEffect(SubscriptionService, ['getPromotion']);

  const { data: subscription, isLoading: isSubLoading } = useApiGetter(
    AdminService,
    'getOrganizationSubscription',
    [orgId, subscriptionId ?? ''],
    { enable: !!subscriptionId },
  );

  const {
    attachOrganizationCard,
    removeOrganizationCard,
    isLoading: isCreditCardMutating,
  } = useApiEffect(AdminService, ['attachOrganizationCard', 'removeOrganizationCard'], {
    revalidate: ['listOrganizationCards'],
  });

  const {
    createOrganizationSubscription,
    updateOrganizationSubscription,
    isLoading: isSubMutating,
  } = useApiEffect(AdminService, ['createOrganizationSubscription', 'updateOrganizationSubscription'], {
    revalidate: ['listOrganizationSubscriptions', 'getOrganizationSubscription', 'listOrganizationLicenseAssignments'],
  });

  const { adminPatchOrganization, isLoading: isOrgMutating } = useApiEffect(
    OrganizationService,
    ['adminPatchOrganization'],
    { revalidate: ['adminGetOrganization'] },
  );

  const { data: tax, isLoading: isTaxLoading } = useApiGetter(
    AdminService,
    'getOrganizationPlanTax',
    [orgId, plan?.id ?? '', seats],
    {
      defaultValue: null,
      enable: !!(
        plan &&
        organization?.billing_details.country &&
        VAT_COUNTRIES.includes(organization.billing_details.country)
      ),
    },
  );

  const isPageLoading = isResourcesLoading || (subscriptionId && isSubLoading);

  const isLoading = isResourcesLoading || isOrgLoading || isOrgMutating;

  const { data: cards } = useApiGetter(AdminService, 'listOrganizationCards', [orgId]);

  const onAddNewCard = async (cardId: string, currency: string) => {
    await attachOrganizationCard(orgId, { token: cardId, currency });
  };

  const onApplyPromoCode = async (planId: string, code: Optional<string>) => {
    setPromotion(code ? await getPromotion(planId, code) : undefined);
  };

  const onSubscribe = async (
    selectedPlanId: string,
    productIds: string[],
    context: OrderContext,
    subId?: string,
    discountId?: string,
  ) => {
    if (subId) {
      const updatedSubscription = await updateOrganizationSubscription(orgId, subId, {
        plan: selectedPlanId,
        products: productIds,
        discount: discountId,
        ...context,
      });

      return goto(AppRoute.ADMIN_ORGANIZATION_SUBSCRIPTION, { orgId, subscriptionId: updatedSubscription.id });
    }

    const createdSubscription = await createOrganizationSubscription(orgId, {
      plan: selectedPlanId,
      products: productIds,
      discount: discountId,
      ...context,
    });

    return goto(AppRoute.ADMIN_ORGANIZATION_SUBSCRIPTION, { orgId, subscriptionId: createdSubscription.id });
  };

  const planId = subscription?.plan.id;
  const plans = resources?.plans;
  const defaultPlan = planId
    ? (plans?.find((p) => p.id === planId) ?? plans?.find((p) => p.supersedes_plan_ids?.includes(planId)))
    : undefined;

  return (
    <AdminLayout title={subscriptionId ? 'Update Subscription' : 'Create Subscription'}>
      {isPageLoading && <CheckoutFormSkeleton />}
      {!isPageLoading && (
        <CheckoutComponent
          adminMode
          adminDiscounts={resources?.discounts ?? []}
          adminProducts={resources?.products ?? []}
          loading={isLoading}
          isSubMutating={isSubMutating}
          taxLoading={isTaxLoading}
          defaultPlan={defaultPlan}
          tax={tax}
          plans={plans ?? []}
          cards={cards ?? []}
          subscription={subscription}
          organization={organization}
          promotion={promotion}
          promotionLoading={isPromotionLoading}
          showCardForm={showCardForm}
          onChangeSeats={setSeats}
          onChangePlan={setPlan}
          onToggleCardForm={() => setShowCardForm(!showCardForm)}
          onApplyPromoCode={onApplyPromoCode}
          onUpdateOrganization={(data) => adminPatchOrganization(orgId, data)}
          onDeleteCard={(card) => removeOrganizationCard(orgId, card.id)}
          onSubscribe={onSubscribe}
          renderCardForm={(currency: string) => (
            <Box p={2}>
              <StripeCardForm
                loading={stripeLoading || isCreditCardMutating}
                onSaveCard={async (cardId) => {
                  try {
                    setStripeLoading(true);
                    await onAddNewCard(cardId, currency);
                    setShowCardForm(false);
                  } finally {
                    setStripeLoading(false);
                  }
                }}
              />
            </Box>
          )}
        />
      )}
    </AdminLayout>
  );
};
